import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../components/Toast";
import { request } from "../../../lib/api";
import { fmtDuration } from "../../../lib/helpers";
import type { CourseDetail, CourseStatus, ExamOut } from "../../../lib/types";

export function PublishTab({ course, exam }: { course: CourseDetail; exam: ExamOut | undefined }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<CourseStatus>(course.status);

  const update = useMutation({
    mutationFn: () => request<CourseDetail>(`/courses/${course.id}`, { method: "PATCH", body: { status } }),
    onSuccess: (updated) => {
      qc.setQueryData(["course", course.id], updated);
      qc.invalidateQueries({ queryKey: ["courses"] });
      toast("Status actualizat", "success");
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  const del = useMutation({
    mutationFn: () => request(`/courses/${course.id}`, { method: "DELETE" }),
    onSuccess: () => { toast("Curs șters", "success"); setTimeout(() => navigate("/admin/cursuri"), 500); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  function statusPill(s: string) {
    if (s === "published") return <span className="pill success">Publicat</span>;
    if (s === "draft") return <span className="pill warning">Schiță</span>;
    return <span className="pill neutral">Arhivat</span>;
  }

  return (
    <div className="grid-2" style={{ alignItems: "start" }}>
      <div className="card">
        <div className="card-head"><h3 style={{ margin: 0 }}>Status publicare</h3>{statusPill(course.status)}</div>
        <div className="form-group mt-16">
          <label>Vizibilitate</label>
          <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value as CourseStatus)}>
            <option value="draft">Schiță (vizibil doar pentru tine)</option>
            <option value="published">Publicat (vizibil pentru cursanți)</option>
            <option value="archived">Arhivat (ascuns din catalog)</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={() => update.mutate()} disabled={update.isPending}>
          <i className="fa-solid fa-rocket"></i> {update.isPending ? "Se aplică..." : "Aplică"}
        </button>
      </div>

      <div className="card">
        <h3>Sumar</h3>
        <div className="grid-2 gap-16">
          <div><small className="muted">Lecții</small><br /><strong>{course.lessons.length}</strong></div>
          <div><small className="muted">Întrebări examen</small><br /><strong>{exam?.questions.length || 0}</strong></div>
          <div><small className="muted">Public țintă</small><br /><strong>{(course.audiences || []).join(", ") || "—"}</strong></div>
          <div><small className="muted">Durată</small><br /><strong>{fmtDuration(course.duration_minutes)}</strong></div>
        </div>
      </div>

      <div className="card" style={{ borderColor: "var(--c-accent)", background: "#fffafb" }}>
        <h3 style={{ color: "var(--c-accent)" }}>Zonă de risc</h3>
        <div className="flex flex-between" style={{ padding: "8px 0" }}>
          <div>
            <strong style={{ color: "var(--c-accent)" }}>Șterge cursul</strong><br />
            <small className="muted">Toate lecțiile și examenul vor fi eliminate. Ireversibil.</small>
          </div>
          <button className="btn btn-accent btn-sm" onClick={() => { if (confirm("Ștergi cursul DEFINITIV?")) del.mutate(); }}>
            <i className="fa-solid fa-trash"></i> Șterge
          </button>
        </div>
      </div>
    </div>
  );
}
